import React, { createContext, useContext, useReducer } from "react"

type CheckoutContextType = {
  activeStepIndex: number
  activeArticle?: any
  formData: {
    [key in string]: any
  }
  product?: {
    isCompany: boolean
    articles: any[]
  }
  steps: object[]
  stepLoading: boolean
  stepLoaderMessage: string
  terms: string
  thankYouPage: string
  type: string
  token: string
  campaignName: string
  membershipAmount: string,
  trackingItem: object
  setActiveStepIndex?: Function
  setUserDetails?: Function
  clearPersonalDetails?: Function
  setFormData?: Function
  setMemorySheet?: Function
  setPaymentAmount?: Function
  setFeeAmount?: Function
  setPaymentMethod?: Function
  setArticle?: Function
  setArticleTitle?: Function
  setNextActiveStep?: Function
  setPreviousActiveStep?: Function
  setDeliveryMethod?: Function
  setDeliveryAddress?: Function
  setMembership?: Function
  setMemberPaperDeliveryMethod?: Function
  setRecruiterData?: Function
  setStepLoading?: Function
  setStepLoaderMessage?: Function
  setQuantity?: Function
  resetContext?: Function
  setToken?: Function
  setS2DenyUrl?: Function
  setCampaignName?: Function
  setTrackingItem?: Function
  setMembershipAmount?: Function
}

type TStep = {
  name: string
}

interface TPaymentAmountStep extends TStep {
  minAmount: number
  manualMinAmount: boolean
}

interface TMembershipTypeStep extends TStep {
  productPrices: { family_member: number; youth_member: number; member: number }
}

type ReducerActionType =
  | {
      type: "RESET"
      payload: { id: number; steps: []; product: any; terms: string, thankYouPage: string, type: string, token: '' }
    }
  | { type: "SET_ACTIVE_STEP_INDEX"; activeStepIndex: number }
  | {
      type:
        | "SET_NEXT_ACTIVE_STEP"
        | "SET_PREVIOUS_ACTIVE_STEP"
        | "CLEAR_PERSONAL_DETAILS"
    }
  | {
      type: "SET_PAYMENT_AMOUNT"
      amount: number
      hasSubmittedPaymentStep: boolean
      skipAmount: boolean
    }
  | {
    type: "SET_FEE_AMOUNT"
    amount: number
    }
  | { type: "SET_STEP_LOADING"; loading: boolean }
  | { type: "SET_STEP_LOADER_MESSAGE"; message: string }
  | { type: "SET_TOKEN"; token: string }
  | { type: "SET_S2_DENY_URL"; s2DenyUrl: string }
  | {
      type: "SET_MEMBER_PAPER_DELIVERY_METHOD"
      memberPaperDeliveryMethod: string
    }
  | {
      type:
        | "SET_FORM_DATA"
        | "SET_USER_DETAILS"
        | "SET_MEMORY_SHEET"
        | "SET_PAYMENT_METHOD"
        | "SET_ARTICLE"
        | "SET_ARTICLE_TITLE"
        | "SET_CAMPAIGN_NAME"
        | "SET_MEMBERSHIP_AMOUNT"
        | "SET_TRACKING_ITEM"
        | "SET_DELIVERY_METHOD"
        | "SET_DELIVERY_ADDRESS"
        | "SET_RECRUITER_DATA"
        | "SET_MEMBERSHIP"
      data: {
        [key in string]: any
      }
    }
  | { type: "SET_QUANTITY"; quantity: number }

const CheckoutContext = createContext<CheckoutContextType>(
  {} as CheckoutContextType
)

const createInitialState = (form: {
  token: string
  id: number
  steps: []
  product: any
  terms: string
  thankYouPage: string,
  type: string,
}) => {
  const { id, steps, product, terms, thankYouPage, type } = form

  const mainPriceArticle = product?.articles.find(
    (article) =>
      article.IsOptional === false &&
      article.PriceType === "Fast pris" &&
      article.Price !== 0
  )

  return {
    activeStepIndex: 0,
    activeArticle: product?.articles[0] ?? null,
    stepLoading: false,
    stepLoaderMessage: "Laddar...",
    terms,
    thankYouPage,
    type,
    campaignName: '',
    trackingItem: [],
    formData: {
      formId: id,
      paymentAmount: {
        amountValue: +mainPriceArticle?.Price || null
      },
      feeAmount : 0,
      userDetails: {
        pin: ""
      },
      quantity: 1
    },
    product,
    steps,
    token: '',
    membershipAmount: '',
  }
}

export const CheckoutProvider = ({
  children,
  form
}: {
  form
  children: React.ReactNode
}) => {
  const [state, dispatch] = useReducer(
    (
      state: CheckoutContextType,
      action: ReducerActionType
    ): CheckoutContextType => {
      switch (action.type) {
        case "SET_ACTIVE_STEP_INDEX":
          return {
            ...state,
            activeStepIndex: action.activeStepIndex
          }

        case "SET_FORM_DATA": {
          const data = action.data
          return {
            ...state,
            formData: {
              ...state.formData,
              data
            }
          }
        }

        case "SET_USER_DETAILS": {
          const currentState = state

          currentState.formData.userDetails = Object.assign(
            state.formData.userDetails,
            action.data
          )

          return Object.assign({}, currentState)
        }

        case "CLEAR_PERSONAL_DETAILS": {
          const currentState = state

          currentState.formData.userDetails = {}

          return Object.assign({}, currentState)
        }

        case "SET_MEMORY_SHEET": {
          const currentState = state

          currentState.formData.memorySheet = action.data

          return Object.assign({}, currentState)
        }

        case "SET_PAYMENT_AMOUNT": {
          return {
            ...state,
            formData: {
              ...state.formData,
              paymentAmount: {
                amountValue: +action.amount,
                hasSubmittedPaymentStep: action.hasSubmittedPaymentStep,
                skipAmount: action.skipAmount
              }
            }
          }
        }

        case "SET_FEE_AMOUNT": {
          return {
            ...state,
            formData: {
              ...state.formData,
                feeAmount: +action.amount
            }
          }
        }

        case "SET_ARTICLE": {
          const currentState = state

          currentState.formData.article = action.data

          return Object.assign({}, currentState)
        }

        case "SET_ARTICLE_TITLE": {
            const currentState = state

            currentState.formData.articleTitle = action.data

            return Object.assign({}, currentState)
          }

        case "SET_CAMPAIGN_NAME": {
            const currentState = state

            currentState.formData.campaignName = action.data

            return Object.assign({}, currentState)
          }

        case "SET_MEMBERSHIP_AMOUNT": {
          const currentState = state

          currentState.formData.membershipAmount = action.data

          return Object.assign({}, currentState)
        }

        case "SET_TRACKING_ITEM": {
            const currentState = state

            currentState.trackingItem = action.data

            return Object.assign({}, currentState)
          }

        case "SET_PAYMENT_METHOD": {
          return {
            ...state,
            formData: {
              ...state.formData,
              paymentMethod: action.data
            }
          }
        }

        case "SET_DELIVERY_METHOD": {
          return {
            ...state,
            formData: {
              ...state.formData,
              deliveryMethod: action.data
            }
          }
        }

        case "SET_MEMBER_PAPER_DELIVERY_METHOD": {
          return {
            ...state,
            formData: {
              ...state.formData,
              memberPaperDeliveryMethod: action.memberPaperDeliveryMethod
            }
          }
        }

        case "SET_DELIVERY_ADDRESS": {
          return {
            ...state,
            formData: {
              ...state.formData,
              deliveryAddress: action.data
            }
          }
        }

        case "SET_MEMBERSHIP": {
          return {
            ...state,
            formData: {
              ...state.formData,
              membership: action.data
            }
          }
        }

        case "SET_QUANTITY": {
          return {
            ...state,
            formData: {
              ...state.formData,
              quantity: action.quantity
            }
          }
        }

        case "SET_RECRUITER_DATA": {
          return {
            ...state,
            formData: {
              ...state.formData,
              recruiterData: action.data
            }
          }
        }

        case "SET_NEXT_ACTIVE_STEP": {
          if (state.steps.length === state.activeStepIndex + 1) return state

          return {
            ...state,
            activeStepIndex: state.activeStepIndex + 1
          }
        }

        case "SET_PREVIOUS_ACTIVE_STEP": {
          if (state.activeStepIndex === 0) return state

          return {
            ...state,
            activeStepIndex: state.activeStepIndex - 1
          }
        }

        case "SET_STEP_LOADING": {
          return {
            ...state,
            stepLoading: action.loading
          }
        }

        case "SET_STEP_LOADER_MESSAGE": {
          return {
            ...state,
            stepLoaderMessage: action.message
          }
        }

        case "SET_TOKEN": {
            return {
              ...state,
              formData: {
                ...state.formData,
                token: action.token
              }
            }
        }

        case "SET_S2_DENY_URL": {
            return {
              ...state,
              formData: {
                ...state.formData,
                s2DenyUrl: action.s2DenyUrl
              }
            }
        }

        case "RESET": {
          return createInitialState(action.payload)
        }

        default:
          return state
      }
    },
    form,
    createInitialState
  )

  const resetContext = () => dispatch({ type: "RESET", payload: form })

  const setActiveStepIndex = (activeStepIndex: number) =>
    dispatch({ type: "SET_ACTIVE_STEP_INDEX", activeStepIndex })

  const setNextActiveStep = () => dispatch({ type: "SET_NEXT_ACTIVE_STEP" })

  const setPreviousActiveStep = () =>
    dispatch({ type: "SET_PREVIOUS_ACTIVE_STEP" })

  const setUserDetails = (data: object) =>
    dispatch({ type: "SET_USER_DETAILS", data })

  const clearPersonalDetails = () =>
    dispatch({ type: "CLEAR_PERSONAL_DETAILS" })

  const setMemorySheet = (data: object) =>
    dispatch({ type: "SET_MEMORY_SHEET", data })

  const setPaymentAmount = (
    amount: number,
    hasSubmittedPaymentStep: boolean,
    skipAmount: boolean
  ) =>
    dispatch({
      type: "SET_PAYMENT_AMOUNT",
      amount,
      hasSubmittedPaymentStep,
      skipAmount
    })

    const setFeeAmount = (
      amount: number,
    ) =>
      dispatch({
        type: "SET_FEE_AMOUNT",
        amount,
      })

  const setFormData = (data: object) =>
    dispatch({ type: "SET_FORM_DATA", data })

  const setPaymentMethod = (data: object) =>
    dispatch({ type: "SET_PAYMENT_METHOD", data })

  const setArticle = (data: object) => dispatch({ type: "SET_ARTICLE", data })

  const setArticleTitle = (data: object) => dispatch({ type: "SET_ARTICLE_TITLE", data })

  const setCampaignName = (data: object) => dispatch({ type: "SET_CAMPAIGN_NAME", data })

  const setMembershipAmount = (data: object) => dispatch({ type: "SET_MEMBERSHIP_AMOUNT", data })

  const setTrackingItem = (data: object) => dispatch({ type: "SET_TRACKING_ITEM", data })

  const setDeliveryMethod = (data: object) =>
    dispatch({ type: "SET_DELIVERY_METHOD", data })

  const setDeliveryAddress = (data: object) =>
    dispatch({ type: "SET_DELIVERY_ADDRESS", data })

  const setMembership = (data: object) =>
    dispatch({ type: "SET_MEMBERSHIP", data })

  const setStepLoading = (loading: boolean) =>
    dispatch({ type: "SET_STEP_LOADING", loading })

  const setStepLoaderMessage = (message: string) =>
    dispatch({ type: "SET_STEP_LOADER_MESSAGE", message })

  const setQuantity = (quantity: number) =>
    dispatch({ type: "SET_QUANTITY", quantity })

  const setMemberPaperDeliveryMethod = (memberPaperDeliveryMethod: string) =>
    dispatch({
      type: "SET_MEMBER_PAPER_DELIVERY_METHOD",
      memberPaperDeliveryMethod
    })

  const setRecruiterData = (data: object) =>
    dispatch({ type: "SET_RECRUITER_DATA", data })

  const setToken = (token: string) =>
    dispatch({ type: "SET_TOKEN", token })

  const setS2DenyUrl = (s2DenyUrl: string) =>
    dispatch({ type: "SET_S2_DENY_URL", s2DenyUrl })

  return (
    <CheckoutContext.Provider
      value={{
        activeStepIndex: state.activeStepIndex,
        activeArticle: state.activeArticle,
        formData: state.formData,
        steps: state.steps,
        terms: state.terms,
        thankYouPage: state.thankYouPage,
        type: state.type,
        product: state.product,
        stepLoading: state.stepLoading,
        stepLoaderMessage: state.stepLoaderMessage,
        token: state.token,
        trackingItem: state.trackingItem,
        campaignName: state.campaignName,
        membershipAmount: state.membershipAmount,
        setActiveStepIndex,
        setStepLoaderMessage,
        setUserDetails,
        clearPersonalDetails,
        setFormData,
        setMemorySheet,
        setPaymentAmount,
        setPaymentMethod,
        setFeeAmount,
        setArticle,
        setArticleTitle,
        setDeliveryMethod,
        setDeliveryAddress,
        setMembership,
        setNextActiveStep,
        setPreviousActiveStep,
        setQuantity,
        setMemberPaperDeliveryMethod,
        setRecruiterData,
        setStepLoading,
        resetContext,
        setToken,
        setS2DenyUrl,
        setCampaignName,
        setTrackingItem,
        setMembershipAmount
      }}>
      {children}
    </CheckoutContext.Provider>
  )
}

const useCheckoutContext = () => useContext(CheckoutContext)

export function useFormData() {
  const { formData } = useCheckoutContext()

  return formData
}

export function useUserDetails() {
  const {
    formData: { userDetails },
    setUserDetails,
    clearPersonalDetails
  } = useCheckoutContext()

  return { userDetails, setUserDetails, clearPersonalDetails }
}

export function useMemorySheet() {
  const {
    formData: { memorySheet },
    setMemorySheet
  } = useCheckoutContext()

  return { memorySheet, setMemorySheet }
}

export function usePaymentAmount() {
  const {
    formData: { paymentAmount },
    setPaymentAmount
  } = useCheckoutContext()

  return { paymentAmount, setPaymentAmount }
}

export function useFeeAmount() {
  const {
    formData: { feeAmount },
    setFeeAmount
  } = useCheckoutContext()

  return { feeAmount, setFeeAmount }
}

export function useProduct() {
  const { product } = useCheckoutContext()

  return product
}

export function useActiveArticle() {
  const { activeArticle } = useCheckoutContext()

  return activeArticle
}

export function usePaymentMethod() {
  const { paymentMethod } = useFormData()
  const { setPaymentMethod } = useCheckoutContext()

  return { paymentMethod, setPaymentMethod }
}

export function useRecruiterData() {
  const { recruiterData } = useFormData()
  const { setRecruiterData } = useCheckoutContext()

  return { recruiterData, setRecruiterData }
}

export function useActiveStep() {
  const { activeStepIndex } = useCheckoutContext()
  const { step } = useStep(activeStepIndex)

  return step
}

export function useStepByName(name: string) {
  const { steps } = useCheckoutContext()

  return steps.find((step: TStep) => step.name === name)
}

export function useStep(index: number) {
  const { steps } = useCheckoutContext()

  return { step: steps[index], isLastStep: index + 1 === steps.length }
}

export function useQuantity() {
  const {
    formData: { quantity },
    setQuantity
  } = useCheckoutContext()

  return { quantity, setQuantity }
}

export function useMemberPaperDeliveryMethod() {
  const {
    formData: { memberPaperDeliveryMethod },
    setMemberPaperDeliveryMethod
  } = useCheckoutContext()

  return { memberPaperDeliveryMethod, setMemberPaperDeliveryMethod }
}

export function useDeliveryAddress() {
  const {
    formData: { deliveryAddress },
    setDeliveryAddress
  } = useCheckoutContext()

  return { deliveryAddress, setDeliveryAddress }
}

export function useArticle() {
  const {
    formData: { article },
    setArticle
  } = useCheckoutContext()

  return { article, setArticle }
}

export function useArticleTitle() {
    const {
      formData: { articleTitle },
      setArticleTitle
    } = useCheckoutContext()

    return { articleTitle, setArticleTitle }
  }

export function useCampaignName() {
    const {
      formData: { campaignName },
      setCampaignName,
    } = useCheckoutContext()
    return { campaignName, setCampaignName }
  }

export function useMembershipAmount() {
  const {
    formData: { membershipAmount },
    setMembershipAmount,
  } = useCheckoutContext()
  return { membershipAmount, setMembershipAmount }
}

export function useTrackingItem() {
    const {
      formData: { trackingItem },
      setTrackingItem
    } = useCheckoutContext()
    return { trackingItem, setTrackingItem }
  }

export function useDeliveryMethod() {
  const {
    formData: { deliveryMethod },
    setDeliveryMethod
  } = useCheckoutContext()

  return { deliveryMethod, setDeliveryMethod }
}

export function useMembership() {
  const {
    formData: { membership },
    setMembership
  } = useCheckoutContext()

  return { type: membership?.type, pin: membership?.pin, members: membership?.members, setMembership }
}

export function useMinAmount() {
  let { minAmount, manualMinAmount } = useStepByName(
    "paymentAmount"
  ) as TPaymentAmountStep
  const membershipStep = useStepByName("membershipType") as TMembershipTypeStep
  const { userDetails } = useUserDetails()
  const { type } = useMembership()

  let amount: number = minAmount

  if (type) {
    amount = membershipStep?.productPrices.member ?? minAmount

    if (type === "familyMembership") {
      amount = membershipStep?.productPrices.family_member ?? minAmount
    }

    if (userDetails.age < 26) {
      amount = membershipStep?.productPrices.youth_member ?? minAmount
    }
  }

  if (manualMinAmount) {
    amount = Math.max(amount, minAmount)
  }

  return amount
}

export function useToken() {
    const {
      formData: { token },
      setToken
    } = useCheckoutContext()

    return { token, setToken }
  }

export function useS2DenyUrl() {
    const {
      formData: { S2DenyUrl },
      setS2DenyUrl
    } = useCheckoutContext()

    return { S2DenyUrl, setS2DenyUrl }
  }

export function useStepLoading() {
  const {
    stepLoading,
    setStepLoading,
    stepLoaderMessage,
    setStepLoaderMessage
  } = useCheckoutContext()

  return {
    stepLoading,
    setStepLoading,
    stepLoaderMessage,
    setStepLoaderMessage
  }
}

export default useCheckoutContext